/**
 * Per-column caption editor for a `button` column. A single text input for the
 * label rendered inside the button (independent of the column header name).
 * Committed only on Save; re-seeded each open from the persisted caption.
 */
import { Input, Label } from '@monksflow/monks-ui';
import { useEffect, useState } from 'react';

import { FormDialog } from '@/shared/ui/FormDialog';
import { useTranslation } from 'react-i18next';

interface ButtonConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current caption for the column being configured. */
  caption: string;
  /** Called with the new caption when the user saves. */
  onSave: (next: string) => void;
}

export function ButtonConfigModal({ open, onOpenChange, caption, onSave }: ButtonConfigModalProps) {
  const { t } = useTranslation('trackers');

  const [draft, setDraft] = useState(caption);
  useEffect(() => {
    if (open) setDraft(caption);
  }, [open, caption]);

  const trimmed = draft.trim();
  const handleSave = () => {
    onSave(trimmed);
    onOpenChange(false);
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('trackersBoard.buttonConfigModal.configureButton')}
    >
      <FormDialog.Body>
        <div className="flex flex-col gap-1">
          <Label htmlFor="button-label-input" className="text-xs text-muted-foreground">
            {t('trackersBoard.buttonConfigModal.buttonLabel')}
          </Label>
          <Input
            id="button-label-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('trackersBoard.buttonConfigModal.eGApproveSendNotify')}
          />
        </div>
      </FormDialog.Body>
      <FormDialog.Footer
        secondary={{
          label: t('trackersBoard.buttonConfigModal.cancel'),
          onClick: () => onOpenChange(false),
        }}
        primary={{
          label: t('trackersBoard.buttonConfigModal.save'),
          onClick: handleSave,
          disabled: trimmed.length === 0,
        }}
      />
    </FormDialog>
  );
}
